import { useEffect, useMemo, useState } from 'react';
import './App.css';
import Hero from './components/Hero';
import EntertainmentSection from './components/EntertainmentSection';
import PartyTools from './components/PartyTools';
import PollWall from './components/PollWall';
import LegalSection from './components/LegalSection';
import LegalPage from './components/LegalPage';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import Footer from './components/Footer';
import { dareList, entertainmentData, spinOptions, truthList } from './data/content';

const STORAGE_KEY = 'party-entertainment-data';

function App() {
  const [spinResult, setSpinResult] = useState('');
  const [dice, setDice] = useState(1);
  const [truth, setTruth] = useState('');
  const [dare, setDare] = useState('');
  const [spinning, setSpinning] = useState(false);
  const [entertainmentPool, setEntertainmentPool] = useState(entertainmentData);
  const [truthPool, setTruthPool] = useState(truthList);
  const [darePool, setDarePool] = useState(dareList);
  const [spinPool, setSpinPool] = useState(spinOptions);
  const [hydrated, setHydrated] = useState(false);
  const [route, setRoute] = useState('home');
  const [polls, setPolls] = useState([
    {
      id: 'p1',
      question: 'What should we play tonight?',
      options: [
        { id: 'o1', text: 'Board games / Word guessing', votes: 3 },
        { id: 'o2', text: 'Movie + playlist', votes: 2 },
        { id: 'o3', text: 'Late-night food / Milk tea', votes: 1 },
      ],
    },
    {
      id: 'p2',
      question: 'Random penalty?',
      options: [
        { id: 'o4', text: 'One truth question', votes: 2 },
        { id: 'o5', text: 'Do 10 squats', votes: 1 },
        { id: 'o6', text: 'Sing a chorus', votes: 1 },
      ],
    },
  ]);

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.replace('#', '');
      const normalized = hash.startsWith('/') ? hash.slice(1) : hash;
      const routeMap = new Set(['legal', 'about', 'contact', 'privacy', 'terms']);
      setRoute(routeMap.has(normalized) ? normalized : 'home');
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // entertainment: merge defaults with saved data and de-duplicate by id
        const savedEntertainment = parsed.entertainmentPool || entertainmentData;
        const mergeCategory = (key) => {
          const defaults = entertainmentData[key] || [];
          const custom = savedEntertainment[key] || [];
          const map = new Map();
          [...defaults, ...custom].forEach((item) => map.set(item.id, item));
          return Array.from(map.values());
        };
        setEntertainmentPool({
          movies: mergeCategory('movies'),
          music: mergeCategory('music'),
          games: mergeCategory('games'),
        });

        const mergedTruth = parsed.truthPool || [...truthList, ...(parsed.customTruth || [])];
        const mergedDare = parsed.darePool || [...dareList, ...(parsed.customDare || [])];
        const mergedSpin = parsed.spinPool || [...spinOptions, ...(parsed.customSpinOptions || [])];
        setTruthPool(mergedTruth);
        setDarePool(mergedDare);
        setSpinPool(mergedSpin);
        setPolls(parsed.polls || []);
      } catch (e) {
        // ignore corrupted data
      }
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ entertainmentPool, truthPool, darePool, spinPool, polls })
    );
  }, [entertainmentPool, truthPool, darePool, spinPool, polls, hydrated]);

  const heroHighlight = useMemo(() => {
    const list = ['PartySpark Hub', 'Party Assistant', 'Offline ready', 'Share easily'];
    return list[Math.floor(Math.random() * list.length)];
  }, []);

  const handleSpin = () => {
    if (spinning) return;
    const pool = [...spinPool];
    setSpinning(true);
    let elapsed = 0;
    let delay = 40;
    const duration = 2400;
    const accelUntil = duration * 0.55;
    const tick = () => {
      const pick = pool[Math.floor(Math.random() * pool.length)];
      setSpinResult(pick);
      elapsed += delay;
      delay = elapsed < accelUntil ? Math.max(28, delay * 0.88) : Math.min(400, delay * 1.14);
      if (elapsed < duration) {
        setTimeout(tick, delay);
      } else {
        setSpinning(false);
      }
    };
    tick();
  };

  const rollDice = () => setDice(Math.floor(Math.random() * 6) + 1);

  const pickTruth = () => {
    setTruth(truthPool[Math.floor(Math.random() * truthPool.length)]);
  };

  const pickDare = () => {
    setDare(darePool[Math.floor(Math.random() * darePool.length)]);
  };

  const handleAddEntertainment = (category, item) => {
    const newItem = { ...item, id: `${category}-${Date.now()}` };
    setEntertainmentPool((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), newItem],
    }));
  };

  const handleDeleteEntertainment = (category, id) => {
    setEntertainmentPool((prev) => ({
      ...prev,
      [category]: (prev[category] || []).filter((item) => item.id !== id),
    }));
  };

  const handleAddTruth = (text) => setTruthPool((prev) => [...prev, text]);
  const handleAddDare = (text) => setDarePool((prev) => [...prev, text]);
  const handleAddSpin = (text) => setSpinPool((prev) => [...prev, text]);
  const handleDeleteTruth = (idx) => setTruthPool((prev) => prev.filter((_, index) => index !== idx));
  const handleDeleteDare = (idx) => setDarePool((prev) => prev.filter((_, index) => index !== idx));
  const handleDeleteSpin = (idx) => setSpinPool((prev) => prev.filter((_, index) => index !== idx));

  const handleAddPoll = (question) => {
    setPolls((prev) => [
      ...prev,
      {
        id: `poll-${Date.now()}`,
        question,
        options: [],
      },
    ]);
  };

  const handleAddOption = (pollId, text) => {
    setPolls((prev) =>
      prev.map((poll) =>
        poll.id === pollId
          ? { ...poll, options: [...poll.options, { id: `${pollId}-${Date.now()}`, text, votes: 0 }] }
          : poll
      )
    );
  };

  const handleVote = (pollId, optionId) => {
    setPolls((prev) =>
      prev.map((poll) =>
        poll.id === pollId
          ? {
              ...poll,
              options: poll.options.map((opt) =>
                opt.id === optionId ? { ...opt, votes: (opt.votes || 0) + 1 } : opt
              ),
            }
          : poll
      )
    );
  };

  const handleDeletePoll = (pollId) => setPolls((prev) => prev.filter((p) => p.id !== pollId));

  const handleDeleteOption = (pollId, optionId) => {
    setPolls((prev) =>
      prev.map((poll) =>
        poll.id === pollId ? { ...poll, options: poll.options.filter((opt) => opt.id !== optionId) } : poll
      )
    );
  };

  const renderHome = () => (
    <>
      <Hero heroHighlight={heroHighlight} spinResult={spinResult} spinning={spinning} onSpin={handleSpin} />
      <main className="content">
        <EntertainmentSection
          entertainmentData={entertainmentPool}
          onAdd={handleAddEntertainment}
          onDelete={handleDeleteEntertainment}
        />
        <PartyTools
          spinResult={spinResult}
          spinning={spinning}
          onSpin={handleSpin}
          spinOptions={spinPool}
          onDeleteSpin={handleDeleteSpin}
          dice={dice}
          onRollDice={rollDice}
          truth={truth}
          dare={dare}
          onPickTruth={pickTruth}
          onPickDare={pickDare}
          onAddTruth={handleAddTruth}
          onAddDare={handleAddDare}
          onAddSpin={handleAddSpin}
          truthOptions={truthPool}
          dareOptions={darePool}
          onDeleteTruth={handleDeleteTruth}
          onDeleteDare={handleDeleteDare}
        />
        <PollWall
          polls={polls}
          onAddPoll={handleAddPoll}
          onAddOption={handleAddOption}
          onVote={handleVote}
          onDeletePoll={handleDeletePoll}
          onDeleteOption={handleDeleteOption}
        />
        <LegalSection />
      </main>
    </>
  );

  const renderLegal = () => <LegalPage />;
  const renderAbout = () => <AboutPage />;
  const renderContact = () => <ContactPage />;
  const renderPrivacy = () => <PrivacyPage />;
  const renderTerms = () => <TermsPage />;

  return (
    <div className="app-shell">
      {route === 'legal'
        ? renderLegal()
        : route === 'about'
        ? renderAbout()
        : route === 'contact'
        ? renderContact()
        : route === 'privacy'
        ? renderPrivacy()
        : route === 'terms'
        ? renderTerms()
        : renderHome()}
      <Footer />
    </div>
  );
}

export default App;
